import React, { useRef, useState } from 'react';
import {
  Button, Box, Dialog, DialogContent, DialogActions,
  Typography, List, ListItem, ListItemIcon, ListItemText, Divider, Chip, Link as MuiLink,
  useTheme,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ─── Known parameters (source: Oracle docs + autoupgrade.jar bytecode) ───────

const GLOBAL_PARAMS = new Set([
  'global_log_dir', 'autoupg_log_dir', 'keystore', 'json_progress_writing_interval',
  // global upgrade-only
  'add_after_upgrade_pfile', 'add_during_upgrade_pfile',
  'del_after_upgrade_pfile', 'del_during_upgrade_pfile', 'upgradexml',
  // global patch-only
  'patch_json',
]);

// Valid for both upgrade and patching
const COMMON_PARAMS = new Set([
  'sid', 'dbname', 'source_home', 'log_dir', 'start_time', 'checklist',
  'source_tns_admin_dir', 'source_ldap_admin_dir', 'run_utlrp', 'env',
  'revert_before_action', 'revert_after_action', 'create_listener',
  'emcli_path', 'em_target_name', 'em_blackout_suffix',
  'download', 'patch', 'parallel_stats_degree', 'rac_rolling', 'drain_timeout',
  'datapatch_terminate_blocking_sessions', 'target_home', 'source_base',
  'restoration', 'dictionary_stats_before', 'dictionary_stats_after',
  'fixed_stats_before', 'rman_catalog_connect_string', 'wincredential',
  'delete_wincredential_file', 'before_action', 'after_action',
  // misc used by tool / jar
  'platform', 'proxy_host', 'proxy_port', 'tde_enabled', 'upgrade_type',
  'target_version',
]);

const UPGRADE_ONLY_PARAMS = new Set([
  'upgradexml', 'upgrade_node', 'tune_setting', 'pdbs', 'exclusion_list',
  'timezone_upg', 'source_tns_admin_dir', 'target_tns_admin_dir', 'target_ldap_admin_dir', 'target_cdb',
  'target_cdb_link', 'drop_win_src_service', 'manage_standbys_clause', 'remove_rac_config',
  'ignore_errors', 'run_dictionary_health', 'target_is_remote', 'skip_tde_key_import',
  'create_oracle_home', 'target_edition',
  'target_pdb_name', 'target_pdb_copy_option', 'keep_source_pdb', 'source_dblink',
  'drop_dblink', 'close_source', 'parallel_pdb_creation_clause', 'keep_pdb_save_state',
  'target_cdbroot_dblink',
  'target_base', 'remove_underscore_parameters', 'raise_compatible',
  'drop_grp_after_upgrade', 'enable_local_undo', 'catctl_options', 'replay',
  'defer_standby_log_shipping', 'stop_redo_apply', 'manage_network_files',
  'patch_in_upgrade_mode', 'export_rman_backup_for_noncdb_to_pdb',
  'del_during_upgrade_pfile', 'add_during_upgrade_pfile',
  'del_after_upgrade_pfile', 'add_after_upgrade_pfile',
]);

const PATCH_ONLY_PARAMS = new Set([
  'patch_json', 'download_folder', 'folder', 'patch_node', 'ignore_conflicts',
  'db_availability', 'method', 'gold_image', 'proxy-server', 'ssh-server',
  'drop_grp_after_patching', 'datapatch_options',
  'before_action.deploy', 'before_action.create_home',
  'after_action.deploy', 'after_action.create_home',
  'revert_before_action.deploy', 'revert_before_action.create_home',
  'revert_after_action.deploy', 'revert_after_action.create_home',
  // home_settings — patching only (per docs and tool behavior)
  'home_settings.binopt.asm', 'home_settings.binopt.dm', 'home_settings.binopt.jox',
  'home_settings.binopt.olap', 'home_settings.binopt.part', 'home_settings.binopt.rac',
  'home_settings.binopt.rat', 'home_settings.binopt.sdo', 'home_settings.binopt.uniaud',
  'home_settings.binopt.dnfs', 'home_settings.cluster_nodes',
  'home_settings.ignore_opatch_conflict', 'home_settings.ignore_prereq_failure',
  'home_settings.inventory_location', 'home_settings.inventory_group',
  'home_settings.osdba_group', 'home_settings.osoper_group',
  'home_settings.osbackupdba_group', 'home_settings.osdgdba_group',
  'home_settings.oskmdba_group', 'home_settings.osracdba_group',
  'home_settings.read_only', 'home_settings.ru_apply', 'home_settings.oracle_base',
  'home_settings.home_name', 'home_settings.edition', 'home_settings.account_type',
  'home_settings.ora_lpenable',
]);

// Upgrade nested params: field can be prefix.PDBNAME (e.g., source_dblink.MYPDB)
const UPGRADE_NESTED_PREFIXES = new Set([
  'source_dblink', 'target_pdb_name', 'target_pdb_copy_option', 'keep_source_pdb',
  'drop_dblink', 'close_source', 'parallel_pdb_creation_clause', 'keep_pdb_save_state',
  'target_cdbroot_dblink',
]);

// Parameters that only accept YES or NO (docs-verified)
const YES_NO_PARAMS = new Set([
  'run_utlrp', 'datapatch_terminate_blocking_sessions', 'delete_wincredential_file',
  'restoration', 'download', 'dictionary_stats_before', 'dictionary_stats_after',
  'fixed_stats_before', 'target_is_remote', 'skip_tde_key_import', 'create_oracle_home',
  'remove_rac_config', 'keep_source_pdb', 'drop_dblink', 'remove_underscore_parameters',
  // raise_compatible accepts Y/N or a version string (e.g. 23.0) — excluded intentionally
  'drop_grp_after_upgrade', 'enable_local_undo', 'replay',
  'defer_standby_log_shipping', 'patch_in_upgrade_mode',
  'export_rman_backup_for_noncdb_to_pdb', 'drop_grp_after_patching',
  'home_settings.ignore_prereq_failure', 'home_settings.ru_apply',
  'home_settings.read_only', 'tde_enabled', 'timezone_upg',
  // stop_redo_apply accepts yes/no OR a list of db_unique_names — excluded intentionally
]);

// Parameters with documented enumerated values (non YES/NO)
const VALID_ENUM_PARAMS: { [param: string]: string[] } = {
  'rac_rolling':                    ['auto', 'required', 'disabled', 'force'],
  'manage_network_files':           ['FULL', 'SKIP', 'IGNORE_READ_ONLY'],
  'gold_image':                     ['NO', 'AUTO', 'YES', 'ALL'],
  'method':                         ['outofplace'],
  'home_settings.ignore_opatch_conflict': ['error', 'keep_first', 'skip_all'],
  'run_dictionary_health':          ['full', 'critical'],
};

// Valid platform values (source: Oracle docs)
const VALID_PLATFORMS = new Set([
  'LINUX.X64', 'WINDOWS.X64', 'AIX.x64', 'ARM.x64',
  'SPARC.x64', 'SOLARIS.x64',
  // legacy values from older autoupgrade versions
  'SOLARIS.SPARC64', 'SOLARIS.X64', 'HPUX.IA64', 'AIX.PPC64',
]);

// ─── Levenshtein distance for typo suggestions ───────────────────────────────

const levenshtein = (a: string, b: string): number => {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (__, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1] : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
  return dp[m][n];
};

const suggestParam = (unknown: string, candidates: string[]): string | null => {
  let best: string | null = null;
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = levenshtein(unknown, c);
    if (d < bestDist) { bestDist = d; best = c; }
  }
  // Only suggest if distance is small relative to the length (max 3 edits, and < 40% of length)
  if (best && bestDist <= 3 && bestDist < unknown.length * 0.4) return best;
  return null;
};

// ─── Parameters that have a UI field in AutoUpgrade Composer ─────────────────

const UI_COVERED_PARAMS = new Set([
  // common
  'sid', 'source_home', 'target_home', 'log_dir', 'start_time',
  'restoration', 'platform', 'tde_enabled', 'source_tns_admin_dir', 'target_tns_admin_dir',
  'proxy_host', 'proxy_port', 'wincredential', 'delete_wincredential_file',
  'download', 'patch', 'target_version',
  // upgrade
  'upgrade_node', 'target_cdb', 'target_pdb_name', 'pdbs', 'source_dblink',
  'target_pdb_copy_option', 'tune_setting', 'drop_grp_after_upgrade', 'timezone_upg',
  'run_utlrp', 'remove_underscore_parameters', 'raise_compatible',
  'del_during_upgrade_pfile', 'add_after_upgrade_pfile',
  'add_during_upgrade_pfile', 'del_after_upgrade_pfile',
  'em_target_name', 'emcli_path', 'em_blackout_suffix',
  'manage_standbys_clause', 'parallel_pdb_creation_clause', 'drop_dblink',
  // patch
  'download_folder', 'folder', 'patch_node', 'drop_grp_after_patching',
  'rac_rolling', 'drain_timeout', 'gold_image', 'env',
  // home_settings — all covered in Home Settings tab
  'home_settings.edition', 'home_settings.oracle_base',
  'home_settings.inventory_location', 'home_settings.inventory_group',
  'home_settings.ignore_prereq_failure', 'home_settings.ignore_opatch_conflict',
  'home_settings.cluster_nodes', 'home_settings.home_name',
  'home_settings.read_only', 'home_settings.ru_apply', 'home_settings.account_type',
  'home_settings.osdba_group', 'home_settings.osoper_group',
  'home_settings.osbackupdba_group', 'home_settings.osdgdba_group',
  'home_settings.oskmdba_group', 'home_settings.osracdba_group',
  // home_settings.binopt.* — all covered in Home Settings tab
  'home_settings.binopt.asm', 'home_settings.binopt.dm', 'home_settings.binopt.jox',
  'home_settings.binopt.olap', 'home_settings.binopt.part', 'home_settings.binopt.rac',
  'home_settings.binopt.rat', 'home_settings.binopt.sdo', 'home_settings.binopt.uniaud',
  'home_settings.binopt.dnfs',
]);

const UI_COVERED_GLOBAL = new Set(['global_log_dir', 'autoupg_log_dir', 'keystore']);

// ─── Types ───────────────────────────────────────────────────────────────────

interface PatchType {
  sid: string;
  sourceHome: string;
  targetHome: string;
  patchFolder: string;
  platform: string;
  patchOptions: {
    recommended: boolean;
    ru: boolean;
    ruVersion: string;
    ojvm: boolean;
    ojvmVersion: string;
    opatch: boolean;
    dpbp: boolean;
    jdk: boolean;
    specificPatches: string;
    ocw?: boolean;
    ocwVersion: string;
  };
  download: 'NO' | 'YES';
  restoration: boolean;
  tdeEnabled: boolean;
  useProxy: boolean;
  proxyHost: string;
  proxyPort: string;
  upgradeType: string;
  distributedUpgrade: boolean;
  targetCdbSid: string;
  newPdbName: string;
  fileNameConvertOption: string;
  sourcePath: string;
  targetPath: string;
  sourcePdb: string;
  targetPdbName: string;
  dbLink: string;
  refreshOption: string;
  refreshInterval: string;
  sourceTnsAdminEnabled: boolean;
  sourceTnsAdminDir: string;
  targetTnsAdminEnabled: boolean;
  targetTnsAdminDir: string;
  upgradeNodeEnabled: boolean;
  upgradeNodeValue: string;
  editionEnabled?: boolean;
  edition?: string;
  oracleBaseEnabled?: boolean;
  oracleBase?: string;
  targetVersionEnabled?: boolean;
  targetVersion?: string;
  startTimeType?: 'datetime' | 'now' | 'delay';
  startTimeValue?: string;
  dropGrpAfterUpgrade: boolean;
  dropGrpAfterPatching: boolean;
  timezoneUpgUpg: boolean;
  runUtlrpUpg: boolean;
  removeUnderscoreUpg: boolean;
  raiseCompatibleUpg: boolean;
  raiseCompatibleUpgValue: string;
  delDuringUpgradePfile: string;
  addAfterUpgradePfile: string;
  delDuringUpgradePfileEnabled: boolean;
  addAfterUpgradePfileEnabled: boolean;
  proactiveFixups: boolean;
  sourceHomeEnabled: boolean;
  addDuringUpgradePfile: string;
  addDuringUpgradePfileEnabled: boolean;
  delAfterUpgradePfile: string;
  delAfterUpgradePfileEnabled: boolean;
  emTargetNameEnabled: boolean;
  emTargetName: string;
  emcliPathEnabled: boolean;
  emcliPath: string;
  emBlackoutSuffixEnabled: boolean;
  emBlackoutSuffix: string;
  oraInventoryEnabled?: boolean;
  oraInventory?: string;
  inventoryGroupEnabled?: boolean;
  inventoryGroup?: string;
  binaryOptionRac?: boolean;
  clusterNodesEnabled?: boolean;
  clusterNodes?: string;
  manageStandbysClause?: string;
  parallelPdbCreation?: string;
  dropDbLink?: boolean;
  passthroughParams: Record<string, string>;
  hsBinoptAsm?: boolean;
  hsBinoptDnfs?: boolean;
  hsBinoptDm?: boolean;
  hsBinoptJox?: boolean;
  hsBinoptOlap?: boolean;
  hsBinoptPart?: boolean;
  hsBinoptRat?: boolean;
  hsBinoptSdo?: boolean;
  hsBinoptUniaud?: boolean;
  hsHomeName?: string;
  ignorePrereqFailure?: boolean;
  hsIgnoreOpatchConflict?: 'error' | 'keep_first' | 'skip_all';
  hsReadOnly?: boolean;
  hsRuApply?: boolean;
  hsAccountType?: string;
  hsOsdbaGroup?: string;
  hsOsoperGroup?: string;
  hsOsbackupdbaGroup?: string;
  hsOsdgdbaGroup?: string;
  hsOskmdbaGroup?: string;
  hsOsracdbaGroup?: string;
  logDir?: string;
  racRolling?: 'DISABLED' | 'REQUIRED' | 'FORCE' | 'AUTO';
  drainTimeout?: string;
}

interface LoadConfigFileProps {
  onConfigLoaded: (data: {
    globalLogDir: string;
    keystorePath: string;
    patches: PatchType[];
    operationType: string;
    executionMode: string;
    tabList: { key: string; label: string }[];
  }) => void;
  buttonOnly?: boolean;
}

interface ValidationResult {
  errors: string[];
  warnings: string[];
  infos: string[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parsePatchOptions = (patchValue: string) => {
  const options = {
    recommended: false,
    ru: false,
    ruVersion: '',
    ojvm: false,
    ojvmVersion: '',
    opatch: false,
    dpbp: false,
    jdk: false,
    specificPatches: '',
    ocw: false,
    ocwVersion: '',
  };

  if (patchValue === 'RECOMMENDED') {
    options.recommended = true;
    return options;
  }

  const parts = patchValue.split(',');
  parts.forEach(part => {
    const trimmed = part.trim();
    if (trimmed.startsWith('RU:')) {
      options.ru = true;
      options.ruVersion = trimmed.substring(3);
    } else if (trimmed === 'RU') {
      options.ru = true;
    } else if (trimmed.startsWith('OJVM:')) {
      options.ojvm = true;
      options.ojvmVersion = trimmed.substring(5);
    } else if (trimmed === 'OJVM') {
      options.ojvm = true;
    } else if (trimmed === 'OPATCH') {
      options.opatch = true;
    } else if (trimmed.startsWith('OCW:')) {
      options.ocw = true;
      options.ocwVersion = trimmed.substring(4);
    } else if (trimmed === 'OCW') {
      options.ocw = true;
    } else if (trimmed === 'DPBP') {
      options.dpbp = true;
    } else if (trimmed === 'JDK') {
      options.jdk = true;
    } else if (trimmed) {
      options.specificPatches = trimmed;
    }
  });

  return options;
};

/** Detect operation type from content when no header comment is present */
const detectOperationType = (
  tabDict: { [tab: string]: { [field: string]: string } },
): string => {
  const tabs = Object.keys(tabDict);
  for (const tab of tabs) {
    // Strong upgrade signals
    if (tab.toLowerCase().startsWith('upg')) return 'upgrade';
    const fields = tabDict[tab];
    // Strong patch signals (checked before upgrade — target_version is patch-only)
    if (tab.toLowerCase().startsWith('patch')) return 'patch';
    if (fields['patch_json'] || fields['method'] || fields['gold_image'] ||
        fields['db_availability'] || fields['download_folder'] || fields['folder'] ||
        fields['patch']) return 'patch';
    // Strong upgrade signals
    if (fields['target_base'] || fields['upgrade_node']) return 'upgrade';
    if (fields['tune_setting'] || fields['pdbs'] || fields['target_cdb']) return 'upgrade';
  }
  // Default: upgrade (more common)
  return 'upgrade';
};

/** Validate all parameters against the known lists */
const validateConfig = (
  globalConfig: { [key: string]: string },
  tabDict: { [tab: string]: { [field: string]: string } },
  operationType: string,
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const infos: string[] = [];
  const isUpgrade = operationType === 'upgrade';
  const validLocal = isUpgrade
    ? new Set(Array.from(COMMON_PARAMS).concat(Array.from(UPGRADE_ONLY_PARAMS)))
    : new Set(Array.from(COMMON_PARAMS).concat(Array.from(PATCH_ONLY_PARAMS)));
  const oppositeParams = isUpgrade ? PATCH_ONLY_PARAMS : UPGRADE_ONLY_PARAMS;
  const oppositeLabel = isUpgrade ? 'patching-only' : 'upgrade-only';
  const allKnownLocal = Array.from(validLocal);

  // Validate global params
  for (const key of Object.keys(globalConfig)) {
    if (!GLOBAL_PARAMS.has(key)) {
      const allGlobal = Array.from(GLOBAL_PARAMS);
      const suggestion = suggestParam(key, allGlobal);
      errors.push(
        `Unknown global parameter: global.${key}` +
        (suggestion ? `\n• Did you mean "global.${suggestion}"?` : '')
      );
    } else if (key === 'autoupg_log_dir') {
      warnings.push(`"global.autoupg_log_dir" is deprecated — use "global.global_log_dir" instead`);
    } else if (!UI_COVERED_GLOBAL.has(key)) {
      infos.push(`"global.${key}" has no corresponding field in AutoUpgrade Composer UI`);
    }
  }

  // Validate per-tab params
  for (const [tab, fields] of Object.entries(tabDict)) {
    // Detect patch execution mode per tab
    const hasDownloadFolder = !!(fields['download_folder'] || fields['folder']);
    const isCreateHomeMode = !isUpgrade && hasDownloadFolder && !!fields['source_home'] && !!fields['target_home'] && !fields['sid'] && !fields['restoration'];
    const isPureDownloadMode = !isUpgrade && hasDownloadFolder && !fields['source_home'] && !fields['sid'];
    const isNonDeployPatchMode = isCreateHomeMode || isPureDownloadMode;
    // Required: sid (not required in download or create_home mode)
    if (!fields['sid'] && !fields['dbname'] && !isNonDeployPatchMode) {
      errors.push(`[${tab}] Missing required parameter: sid`);
    }
    // source_home: required for patching deploy mode; not required for download or create_home
    if (!fields['source_home'] && !isUpgrade && !isPureDownloadMode) {
      errors.push(`[${tab}] Missing required parameter: source_home`);
    }
    // Upgrade: target_home is required
    if (isUpgrade && !fields['target_home']) {
      errors.push(`[${tab}] Missing required parameter: target_home`);
    }
    // Upgrade: target_version required only if target_home absent or is 12.2
    if (isUpgrade && !fields['target_version']) {
      const th = fields['target_home'] || '';
      const is122 = th.includes('12.2') || th.includes('12.2.0');
      if (!th || is122) {
        warnings.push(`[${tab}] "target_version" recommended when target_home is absent or release 12.2`);
      }
    }
    // Patching: download_folder is recommended
    if (!isUpgrade && !fields['download_folder'] && !fields['folder']) {
      warnings.push(`[${tab}] Missing recommended parameter: folder (patch folder)`);
    }

    for (const [field, value] of Object.entries(fields)) {

      // Nested upgrade params: prefix.SUFFIX (e.g., source_dblink.MYPDB)
      const dotIdx = field.indexOf('.');
      if (dotIdx > -1 && isUpgrade) {
        const prefix = field.substring(0, dotIdx);
        if (UPGRADE_NESTED_PREFIXES.has(prefix)) {
          // Valid nested param — check UI coverage by prefix
          if (!UI_COVERED_PARAMS.has(prefix)) {
            infos.push(`[${tab}] "${tab}.${field}" has no corresponding field in AutoUpgrade Composer UI`);
          }
          continue;
        }
      }

      // Check if known
      if (!validLocal.has(field)) {
        if (oppositeParams.has(field)) {
          errors.push(`[${tab}] "${field}" is a ${oppositeLabel} parameter`);
        } else {
          const suggestion = suggestParam(field, allKnownLocal);
          errors.push(
            `[${tab}] Unknown parameter: "${field}"` +
            (suggestion ? `\n• Did you mean "${tab}.${suggestion}"?` : '')
          );
        }
        continue;
      }

      // YES/NO value check
      if (YES_NO_PARAMS.has(field) && value.toUpperCase() !== 'YES' && value.toUpperCase() !== 'NO') {
        errors.push(`[${tab}] "${field}" must be YES or NO (got: "${value}")`);
      }

      // Enumerated value check
      if (VALID_ENUM_PARAMS[field] && value && !VALID_ENUM_PARAMS[field].includes(value)) {
        errors.push(
          `[${tab}] "${field}" has invalid value "${value}". Valid values: ${VALID_ENUM_PARAMS[field].join(', ')}`
        );
      }

      // Platform validation
      if (field === 'platform' && value && !VALID_PLATFORMS.has(value)) {
        errors.push(
          `[${tab}] "platform" has invalid value "${value}". Valid values: ${Array.from(VALID_PLATFORMS).join(', ')}`
        );
      }

      // UI coverage check
      if (!UI_COVERED_PARAMS.has(field)) {
        infos.push(`[${tab}] "${tab}.${field}" has no corresponding field in AutoUpgrade Composer UI`);
      }
    }
  }

  return { errors, warnings, infos };
};

// ─── Component ───────────────────────────────────────────────────────────────

const LoadConfigFile: React.FC<LoadConfigFileProps> = ({ onConfigLoaded, buttonOnly = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dialogBg = isDark ? '#181c23' : theme.palette.background.paper;
  const errBg    = isDark ? 'rgba(183,28,28,0.22)'   : 'rgba(211,47,47,0.08)';
  const errBord  = isDark ? 'rgba(239,83,80,0.45)'   : 'rgba(211,47,47,0.4)';
  const errText  = isDark ? '#ffcdd2'                 : theme.palette.error.dark;
  const errLabel = isDark ? '#ef9a9a'                 : theme.palette.error.main;
  const warnBg   = isDark ? 'rgba(230,119,0,0.18)'   : 'rgba(237,108,2,0.08)';
  const warnBord = isDark ? 'rgba(255,167,38,0.45)'  : 'rgba(237,108,2,0.4)';
  const warnText = isDark ? '#ffe0b2'                 : theme.palette.warning.dark;
  const warnLabel= isDark ? '#ffcc80'                 : theme.palette.warning.dark;
  const infoBg   = isDark ? 'rgba(41,182,246,0.08)'  : 'rgba(2,136,209,0.06)';
  const infoBord = isDark ? 'rgba(41,182,246,0.25)'  : 'rgba(2,136,209,0.3)';
  const infoText = isDark ? '#b3e5fc'                 : theme.palette.info.dark;
  const infoLabel= isDark ? '#81d4fa'                 : theme.palette.info.dark;
  const footerBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)';
  const chipUpgradeTypeBg   = isDark ? 'rgba(156,39,176,0.18)'  : 'rgba(156,39,176,0.80)';
  const chipUpgradeTypeClr  = isDark ? '#ce93d8'                : '#fff';
  const chipUpgradeTypeBord = isDark ? 'rgba(156,39,176,0.4)'   : 'rgba(156,39,176,0.95)';
  const chipDbsBg   = isDark ? 'rgba(38,166,154,0.20)' : 'rgba(38,166,154,0.80)';
  const chipDbsClr  = isDark ? '#80cbc4'               : '#fff';
  const chipDbsBord = isDark ? 'rgba(38,166,154,0.45)' : 'rgba(38,166,154,0.95)';
  const chipNotesBg   = isDark ? 'rgba(41,182,246,0.15)' : 'rgba(41,182,246,0.80)';
  const chipNotesClr  = isDark ? '#81d4fa'               : '#fff';
  const chipNotesBord = isDark ? 'rgba(41,182,246,0.35)' : 'rgba(41,182,246,0.95)';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dialog, setDialog] = useState<{
    open: boolean;
    operationType: string;
    executionMode: string;
    upgradeType: string;
    tabs: string[];
    validation: ValidationResult;
    parsedData: Parameters<typeof onConfigLoaded>[0] | null;
    fromComposer: boolean;
  } | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    event.target.value = '';

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const lines = content.split('\n');

      // ── Detect header ──────────────────────────────────────────────────
      const fromComposer = lines[0]?.startsWith('# Created by AutoUpgrade Composer');
      let operationType = 'patch';
      let executionMode = 'deploy';
      let upgType = '';
      let fileNameConvertOption = '';
      let refreshOption = '';

      if (fromComposer && lines.length > 1) {
        if (lines[1].startsWith('# Patch, ExecMode:')) {
          operationType = 'patch';
          const m = lines[1].match(/ExecMode: ([^,\n]+)/);
          if (m) {
            const mode = m[1].trim().toLowerCase();
            if (mode === 'deploy') executionMode = 'deploy';
            else if (mode === 'analyze') executionMode = 'analyze';
            else if (mode === 'fixups') executionMode = 'fixups';
            else if (mode === 'download') executionMode = 'download';
            else if (mode === 'createhome') executionMode = 'create_home';
          }
        } else if (lines[1].startsWith('# Upgrade, UpgType:')) {
          operationType = 'upgrade';
          const upgMatch = lines[1].match(/UpgType: ([^,\n]+)/);
          if (upgMatch) { upgType = upgMatch[1].trim(); executionMode = upgType; }
          const fnMatch = lines[1].match(/FileNameConvert: ([^,\n]+)/);
          if (fnMatch) {
            fileNameConvertOption = fnMatch[1].trim();
            if (fileNameConvertOption === 'paths') fileNameConvertOption = 'convert';
          }
          const refMatch = lines[1].match(/Refresh: ([^,\n]+)/);
          if (refMatch) refreshOption = refMatch[1].trim();
        }
      }

      // ── Parse key=value entries ────────────────────────────────────────
      const tabDict: { [tab: string]: { [field: string]: string } } = {};
      const globalConfig: { [key: string]: string } = {};
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) continue;
        if (trimmed.startsWith('global.')) {
          const eqIdx = trimmed.indexOf('=');
          if (eqIdx > -1) {
            const gk = trimmed.substring(7, eqIdx);
            const gv = trimmed.substring(eqIdx + 1);
            globalConfig[gk] = gv;
          }
          continue;
        }
        const m = trimmed.match(/^([^.]+)\.(.+?)=(.*)$/);
        if (!m) continue;
        const [, tab, field, value] = m;
        if (!tabDict[tab]) tabDict[tab] = {};
        tabDict[tab][field] = value;
      }

      // ── Detect operation type if no header ─────────────────────────────
      if (!fromComposer) {
        operationType = detectOperationType(tabDict);
        if (operationType === 'upgrade') {
          executionMode = 'regular';
        } else {
          // Detect patch execution mode
          const patchFields = Object.values(tabDict);
          const hasDownloadFolder = patchFields.some(f => f['download_folder'] || f['folder']);
          const hasSourceHome = patchFields.some(f => f['source_home']);
          const hasTargetHome = patchFields.some(f => f['target_home']);
          const hasSid = patchFields.some(f => f['sid']);
          const hasRestoration = patchFields.some(f => f['restoration']);
          // create_home: has download_folder + source_home + target_home but NO sid/restoration (those are deploy-specific)
          if (hasDownloadFolder && hasSourceHome && hasTargetHome && !hasSid && !hasRestoration) {
            executionMode = 'create_home';
          } else if (hasDownloadFolder && !hasSourceHome && !hasSid) {
            executionMode = 'download';
          } else {
            executionMode = 'deploy';
          }
        }
      }

      // ── Validate ───────────────────────────────────────────────────────
      const validation = validateConfig(globalConfig, tabDict, operationType);

      // ── Build patch objects ────────────────────────────────────────────
      const patches: PatchType[] = Object.entries(tabDict).map(([, fields]) => {
        const patch: PatchType = {
          sid: '',
          sourceHome: '',
          targetHome: '',
          patchFolder: '',
          platform: 'LINUX.X64',
          patchOptions: {
            recommended: false,
            ru: true,
            ruVersion: '',
            ojvm: false,
            ojvmVersion: '',
            opatch: true,
            dpbp: false,
            jdk: false,
            specificPatches: '',
            ocwVersion: '',
          },
          download: 'NO',
          restoration: true,
          tdeEnabled: false,
          useProxy: false,
          proxyHost: '',
          proxyPort: '',
          upgradeType: '',
          distributedUpgrade: false,
          targetCdbSid: '',
          newPdbName: '',
          fileNameConvertOption: '',
          sourcePath: '',
          targetPath: '',
          sourcePdb: '',
          targetPdbName: '',
          dbLink: '',
          refreshOption: '',
          refreshInterval: '',
          sourceTnsAdminEnabled: false,
          sourceTnsAdminDir: '',
          targetTnsAdminEnabled: false,
          targetTnsAdminDir: '',
          upgradeNodeEnabled: false,
          upgradeNodeValue: '',
          editionEnabled: false,
          edition: '',
          oracleBaseEnabled: false,
          oracleBase: '/u01/app/oracle',
          targetVersionEnabled: true,
          targetVersion: '',
          startTimeType: undefined,
          startTimeValue: undefined,
          dropGrpAfterUpgrade: false,
          dropGrpAfterPatching: false,
          timezoneUpgUpg: true,
          runUtlrpUpg: true,
          removeUnderscoreUpg: true,
          raiseCompatibleUpg: false,
          raiseCompatibleUpgValue: '',
          delDuringUpgradePfile: '',
          addAfterUpgradePfile: '',
          delDuringUpgradePfileEnabled: false,
          addAfterUpgradePfileEnabled: false,
          proactiveFixups: false,
          sourceHomeEnabled: false,
          addDuringUpgradePfile: '',
          addDuringUpgradePfileEnabled: false,
          delAfterUpgradePfile: '',
          delAfterUpgradePfileEnabled: false,
          emTargetNameEnabled: false,
          emTargetName: '',
          emcliPathEnabled: false,
          emcliPath: '',
          emBlackoutSuffixEnabled: false,
          emBlackoutSuffix: '',
          oraInventoryEnabled: false,
          oraInventory: '',
          inventoryGroupEnabled: false,
          inventoryGroup: '',
          binaryOptionRac: undefined,
          clusterNodesEnabled: false,
          clusterNodes: '',
          manageStandbysClause: '',
          passthroughParams: {},
        };

        for (const [field, value] of Object.entries(fields)) {
          switch (field) {
            case 'sid': patch.sid = value; break;
            case 'source_home': patch.sourceHome = value; patch.sourceHomeEnabled = true; break;
            case 'target_home': patch.targetHome = value; break;
            case 'download_folder': patch.patchFolder = value; break;
            case 'folder': patch.patchFolder = value; break; // deprecated alias
            case 'platform': patch.platform = value; break;
            case 'patch': patch.patchOptions = parsePatchOptions(value); break;
            case 'download': patch.download = value as 'NO' | 'YES'; break;
            case 'restoration': patch.restoration = value === 'YES'; break;
            case 'proxy_host': patch.proxyHost = value; patch.useProxy = true; break;
            case 'proxy_port': patch.proxyPort = value; patch.useProxy = true; break;
            case 'upgrade_type': patch.upgradeType = value; break;
            case 'tune_setting':
              patch.proactiveFixups = value.includes('proactive_fixups=true');
              patch.distributedUpgrade = value.includes('distributed_upgrade=yes');
              break;
            case 'upgrade_node': patch.upgradeNodeEnabled = true; patch.upgradeNodeValue = value; break;
            case 'target_cdb': patch.targetCdbSid = value; break;
            case 'target_pdb_name': patch.newPdbName = value; break;
            case 'pdbs': patch.sourcePdb = value; break;
            case 'source_tns_admin_dir': patch.sourceTnsAdminEnabled = true; patch.sourceTnsAdminDir = value; break;
            case 'target_tns_admin_dir': patch.targetTnsAdminEnabled = true; patch.targetTnsAdminDir = value; break;
            case 'tde_enabled': patch.tdeEnabled = value === 'YES'; break;
            case 'home_settings.edition': patch.edition = value; patch.editionEnabled = true; break;
            case 'home_settings.oracle_base': patch.oracleBase = value; patch.oracleBaseEnabled = true; break;
            case 'home_settings.inventory_location': patch.oraInventory = value; patch.oraInventoryEnabled = true; break;
            case 'home_settings.inventory_group': patch.inventoryGroup = value; patch.inventoryGroupEnabled = true; break;
            case 'home_settings.binopt.rac': patch.binaryOptionRac = value === 'yes'; break;
            case 'home_settings.binopt.asm': patch.hsBinoptAsm = value === 'yes'; break;
            case 'home_settings.binopt.dnfs': patch.hsBinoptDnfs = value === 'yes'; break;
            case 'home_settings.binopt.dm': patch.hsBinoptDm = value === 'yes'; break;
            case 'home_settings.binopt.jox': patch.hsBinoptJox = value === 'yes'; break;
            case 'home_settings.binopt.olap': patch.hsBinoptOlap = value === 'yes'; break;
            case 'home_settings.binopt.part': patch.hsBinoptPart = value === 'yes'; break;
            case 'home_settings.binopt.rat': patch.hsBinoptRat = value === 'yes'; break;
            case 'home_settings.binopt.sdo': patch.hsBinoptSdo = value === 'yes'; break;
            case 'home_settings.binopt.uniaud': patch.hsBinoptUniaud = value === 'yes'; break;
            case 'home_settings.cluster_nodes': patch.clusterNodes = value; patch.clusterNodesEnabled = true; break;
            case 'home_settings.home_name': patch.hsHomeName = value; break;
            case 'home_settings.ignore_prereq_failure': patch.ignorePrereqFailure = value === 'YES'; break;
            case 'home_settings.ignore_opatch_conflict': patch.hsIgnoreOpatchConflict = value as any; break;
            case 'home_settings.read_only': patch.hsReadOnly = value === 'yes'; break;
            case 'home_settings.ru_apply': patch.hsRuApply = value === 'yes'; break;
            case 'home_settings.account_type': patch.hsAccountType = value; break;
            case 'home_settings.osdba_group': patch.hsOsdbaGroup = value; break;
            case 'home_settings.osoper_group': patch.hsOsoperGroup = value; break;
            case 'home_settings.osbackupdba_group': patch.hsOsbackupdbaGroup = value; break;
            case 'home_settings.osdgdba_group': patch.hsOsdgdbaGroup = value; break;
            case 'home_settings.oskmdba_group': patch.hsOskmdbaGroup = value; break;
            case 'home_settings.osracdba_group': patch.hsOsracdbaGroup = value; break;
            case 'log_dir': patch.logDir = value; break;
            case 'rac_rolling': patch.racRolling = value as any; break;
            case 'drain_timeout': patch.drainTimeout = value; break;
            case 'target_version': patch.targetVersion = value; patch.targetVersionEnabled = true; break;
            case 'start_time': patch.startTimeType = 'datetime'; patch.startTimeValue = value; break;
            case 'drop_grp_after_upgrade': patch.dropGrpAfterUpgrade = value === 'YES'; break;
            case 'drop_grp_after_patching': patch.dropGrpAfterPatching = value === 'YES'; break;
            case 'gold_image': (patch.patchOptions as any).goldImage = value; break;
            case 'timezone_upg': patch.timezoneUpgUpg = value !== 'NO'; break;
            case 'run_utlrp': patch.runUtlrpUpg = value !== 'NO'; break;
            case 'remove_underscore_parameters': patch.removeUnderscoreUpg = value !== 'NO'; break;
            case 'raise_compatible':
              patch.raiseCompatibleUpg = true;
              patch.raiseCompatibleUpgValue = value !== 'YES' ? value : '';
              break;
            case 'del_during_upgrade_pfile': patch.delDuringUpgradePfile = value; patch.delDuringUpgradePfileEnabled = !!value; break;
            case 'add_after_upgrade_pfile': patch.addAfterUpgradePfile = value; patch.addAfterUpgradePfileEnabled = !!value; break;
            case 'add_during_upgrade_pfile': patch.addDuringUpgradePfile = value; patch.addDuringUpgradePfileEnabled = !!value; break;
            case 'del_after_upgrade_pfile': patch.delAfterUpgradePfile = value; patch.delAfterUpgradePfileEnabled = !!value; break;
            case 'em_target_name': patch.emTargetName = value; patch.emTargetNameEnabled = !!value; break;
            case 'emcli_path': patch.emcliPath = value; patch.emcliPathEnabled = !!value; break;
            case 'em_blackout_suffix': patch.emBlackoutSuffix = value; patch.emBlackoutSuffixEnabled = !!value; break;
            case 'manage_standbys_clause':
              patch.manageStandbysClause = value;
              break;
            case 'drop_dblink': patch.dropDbLink = value.toLowerCase() === 'yes'; break;
            default:
              if (field.startsWith('parallel_pdb_creation_clause.')) {
                const n = Number(value);
                patch.parallelPdbCreation = !isNaN(n) && n > 0 ? String(n) : '';
              } else if (field.startsWith('source_dblink.')) {
                const [dbLink, refresh] = value.split(' ');
                patch.dbLink = dbLink;
                if (refresh && refresh !== 'CLONE_ONLY') {
                  const seconds = Number(refresh);
                  patch.refreshOption = 'interval';
                  patch.refreshInterval = !isNaN(seconds) && seconds > 0 ? String(Math.round(seconds / 60)) : '';
                } else if (refresh === 'CLONE_ONLY') {
                  patch.refreshOption = 'clone_only';
                  patch.refreshInterval = '';
                }
              } else if (field.startsWith('target_pdb_name.')) {
                patch.targetPdbName = value;
              } else if (field.startsWith('target_pdb_copy_option.')) {
                if (value === 'file_name_convert=NONE') {
                  patch.fileNameConvertOption = 'none';
                  patch.sourcePath = '';
                  patch.targetPath = '';
                } else if (value.startsWith("file_name_convert=('")) {
                  patch.fileNameConvertOption = 'convert';
                  const m = value.match(/\('(.+)', '(.+)'\)/);
                  if (m) { patch.sourcePath = m[1]; patch.targetPath = m[2]; }
                } else if (value.startsWith('file_name_convert')) {
                  patch.fileNameConvertOption = 'nocopy';
                }
              } else if (!UI_COVERED_PARAMS.has(field)) {
                // Only preserve if it's a known/valid param — skip unknown params (already shown as errors)
                const isKnown = COMMON_PARAMS.has(field) || UPGRADE_ONLY_PARAMS.has(field) || PATCH_ONLY_PARAMS.has(field);
                if (isKnown) patch.passthroughParams[field] = value;
              }
              break;
          }
        }

        patch.upgradeType = upgType || patch.upgradeType || 'regular';
        // Auto-detect upgrade type for external files (no Composer header)
        if (!fromComposer && patch.upgradeType === 'regular') {
          if (patch.dbLink) {
            patch.upgradeType = 'pdbrelocation';
          } else if (patch.targetCdbSid && (patch.newPdbName || patch.targetPdbName)) {
            patch.upgradeType = 'noncdb2cdb';
          } else if (patch.manageStandbysClause) {
            // Any value of manage_standbys_clause signals an unplug-plug upgrade
            patch.upgradeType = 'unplugplug';
          }
        }
        if (!patch.fileNameConvertOption) patch.fileNameConvertOption = fileNameConvertOption || '';
        if (!patch.refreshOption && upgType === 'pdbrelocation') patch.refreshOption = refreshOption || '';

        const isWin = (s: string) => typeof s === 'string' && /^[A-Za-z]:\\/.test(s);
        if (isWin(patch.sourceHome) || isWin(patch.targetHome) || isWin(patch.patchFolder) || isWin(patch.oracleBase || '')) {
          patch.platform = 'WINDOWS.X64';
        }

        return patch;
      });

      const tabList = Object.keys(tabDict).map(tab => ({ key: tab, label: tab }));
      const parsedData = {
        globalLogDir: globalConfig['global_log_dir'] || globalConfig['autoupg_log_dir'] || '',
        keystorePath: globalConfig['keystore'] || '',
        patches,
        operationType,
        executionMode,
        tabList,
      };

      setDialog({
        open: true,
        operationType,
        executionMode,
        upgradeType: patches[0]?.upgradeType || 'regular',
        tabs: Object.keys(tabDict),
        validation,
        parsedData,
        fromComposer,
      });
    };

    reader.readAsText(file);
  };

  const handleLoad = () => {
    if (dialog?.parsedData) {
      onConfigLoaded(dialog.parsedData);
    }
    setDialog(null);
  };

  const handleClose = () => setDialog(null);



  // ── Validation Dialog ──────────────────────────────────────────────────────
  const hasWarnings = (dialog?.validation.warnings.length ?? 0) > 0;
  const hasErrors   = (dialog?.validation.errors.length   ?? 0) > 0;
  const hasInfos    = (dialog?.validation.infos.length    ?? 0) > 0;

  const bannerColor = hasErrors ? '#c62828' : '#1b5e20';

  const bannerIcon = hasErrors
    ? <ErrorOutlineIcon       sx={{ fontSize: 48, color: '#fff' }} />
    : <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#fff' }} />;

  const bannerTitle = hasErrors ? 'Configuration has Errors' : 'Configuration is Valid';

  const ValidationDialog = (
    <Dialog
      open={!!dialog?.open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', bgcolor: dialogBg } }}
    >
      {/* ── Colored banner header ── */}
      <Box
        sx={{
          background: bannerColor,
          px: 3,
          pt: 3,
          pb: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {bannerIcon}
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff', lineHeight: 1.2 }}>
            {bannerTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.3 }}>
            {dialog?.fromComposer ? 'Generated with AutoUpgrade Composer' : 'Not generated with AutoUpgrade Composer'}
          </Typography>
        </Box>
      </Box>

      {/* ── Summary pills ── */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          px: 3,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Chip
          icon={<InfoOutlinedIcon />}
          label={dialog?.operationType === 'upgrade' ? 'Upgrade' : 'Patching'}
          size="small"
          color="info"
          sx={{ fontWeight: 600 }}
        />
        {dialog?.operationType === 'upgrade' && (() => {
          const ut = dialog.upgradeType;
          const label = ut === 'pdbrelocation' ? 'PDB Relocation'
            : ut === 'noncdb2cdb' ? 'Non-CDB to CDB'
            : ut === 'unplugplug' ? 'Unplug-Plug'
            : 'Regular Upgrade';
          return (
            <Chip
              label={label}
              size="small"
              sx={{ fontWeight: 600, bgcolor: chipUpgradeTypeBg, color: chipUpgradeTypeClr, border: `1px solid ${chipUpgradeTypeBord}`, '& .MuiChip-icon': { color: chipUpgradeTypeClr } }}
            />
          );
        })()}
        {dialog?.operationType !== 'upgrade' && (() => {
          const em = dialog?.executionMode || 'deploy';
          const label = em === 'download' ? 'Download'
            : em === 'create_home' ? 'Create Home'
            : 'Deploy';
          return (
            <Chip
              label={label}
              size="small"
              sx={{ fontWeight: 600, bgcolor: chipUpgradeTypeBg, color: chipUpgradeTypeClr, border: `1px solid ${chipUpgradeTypeBord}` }}
            />
          );
        })()}
        <Chip
          label={`${dialog?.tabs.length ?? 0} database${(dialog?.tabs.length ?? 0) !== 1 ? 's' : ''}`}
          size="small"
          sx={{ fontWeight: 600, bgcolor: chipDbsBg, color: chipDbsClr, border: `1px solid ${chipDbsBord}` }}
        />
        {hasErrors && (
          <Chip
            icon={<ErrorOutlineIcon />}
            label={`${dialog!.validation.errors.length} error${dialog!.validation.errors.length > 1 ? 's' : ''}`}
            size="small"
            color="error"
            sx={{ fontWeight: 600 }}
          />
        )}
        {hasWarnings && (
          <Chip
            icon={<WarningAmberIcon />}
            label={`${dialog!.validation.warnings.length} warning${dialog!.validation.warnings.length > 1 ? 's' : ''}`}
            size="small"
            color="warning"
            sx={{ fontWeight: 600 }}
          />
        )}
        {!hasErrors && !hasWarnings && (
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label="No issues found"
            size="small"
            color="success"
            sx={{ fontWeight: 600 }}
          />
        )}
        {hasInfos && (
          <Chip
            icon={<InfoOutlinedIcon />}
            label={`${dialog!.validation.infos.length} note${dialog!.validation.infos.length > 1 ? 's' : ''}`}
            size="small"
            sx={{ fontWeight: 600, bgcolor: chipNotesBg, color: chipNotesClr, border: `1px solid ${chipNotesBord}`, '& .MuiChip-icon': { color: chipNotesClr } }}
          />
        )}
      </Box>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* Errors */}
        {hasErrors && (
          <Box sx={{ mb: hasWarnings ? 2 : 0 }}>
            <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: errLabel }}>
              Errors
            </Typography>
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {dialog!.validation.errors.map((msg, i) => (
                <ListItem
                  key={i}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    mb: 0.5,
                    borderRadius: 1.5,
                    bgcolor: errBg,
                    border: `1px solid ${errBord}`,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <ErrorOutlineIcon sx={{ color: 'error.main' }} fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={msg}
                    primaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace', fontSize: '0.78rem', color: errText, whiteSpace: 'pre-wrap' } }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Warnings */}
        {hasWarnings && (
          <Box>
            {hasErrors && <Divider sx={{ mb: 1.5 }} />}
            <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: warnLabel }}>
              Warnings
            </Typography>
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {dialog!.validation.warnings.map((msg, i) => (
                <ListItem
                  key={i}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    mb: 0.5,
                    borderRadius: 1.5,
                    bgcolor: warnBg,
                    border: `1px solid ${warnBord}`,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <WarningAmberIcon sx={{ color: 'warning.main' }} fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={msg}
                    primaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace', fontSize: '0.78rem', color: warnText } }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Notes — valid params not covered in Composer UI */}
        {hasInfos && (
          <Box sx={{ mt: hasErrors || hasWarnings ? 2 : 0 }}>
            {(hasErrors || hasWarnings) && <Divider sx={{ mb: 1.5 }} />}
            <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: infoLabel }}>
              Notes — Not in Composer UI
            </Typography>
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {dialog!.validation.infos.map((msg, i) => (
                <ListItem
                  key={i}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    mb: 0.5,
                    borderRadius: 1.5,
                    bgcolor: infoBg,
                    border: `1px solid ${infoBord}`,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <InfoOutlinedIcon sx={{ color: 'info.main' }} fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={msg}
                    primaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace', fontSize: '0.78rem', color: infoText } }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* All clear */}
        {!hasErrors && !hasWarnings && (
          <Box
            sx={{
              textAlign: 'center',
              py: 3,
              color: 'text.secondary',
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="body2">
              All parameters are recognized and values are valid.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* ── Documentation links ── */}
      <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: footerBg }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, color: 'text.primary', display: 'block', mb: 1 }}>
          AutoUpgrade Parameters Documentation:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {[
          { label: 'Common', href: 'https://docs.oracle.com/en/database/oracle/oracle-database/19/upgrd/common-parameters-autoupgrade-config-file.html' },
          { label: 'Upgrade-only', href: 'https://docs.oracle.com/en/database/oracle/oracle-database/19/upgrd/upgrade-parameters-autoupgrade-config-file.html' },
          { label: 'Patch-only', href: 'https://docs.oracle.com/en/database/oracle/oracle-database/19/upgrd/patch-parameters-autoupgrade-config-file.html' },
        ].map(({ label, href }) => (
          <MuiLink
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.4,
              borderRadius: 5,
              fontSize: '0.72rem',
              fontWeight: 700,
              bgcolor: '#1565c0',
              color: '#ffffff',
              border: '1px solid #1e88e5',
              letterSpacing: 0.4,
              '&:hover': { bgcolor: '#1976d2', color: '#ffffff' },
            }}
          >
            {label} ↗
          </MuiLink>
        ))}
        </Box>
      </Box>

      {hasErrors && (
        <Box sx={{ px: 3, pt: 1.5, pb: 0 }}>
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500, mb: 1.5 }}>
            ⚠ Parameters flagged as errors will not be loaded — only valid parameters are applied.
          </Typography>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />
        </Box>
      )}
      <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} variant="outlined" color="inherit" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleLoad}
          variant="contained"
          color={hasErrors ? 'warning' : 'primary'}
          startIcon={hasErrors ? <WarningAmberIcon /> : <CheckCircleOutlineIcon />}
          sx={{ borderRadius: 2, fontWeight: 'bold' }}
        >
          {hasErrors ? 'Load Anyway' : 'Load'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Button (buttonOnly mode) ───────────────────────────────────────────────
  if (buttonOnly) {
    return (
      <>
        <input
          type="file"
          accept=".cfg"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileSelect}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            fontWeight: 'bold',
            borderRadius: 8,
            px: 4,
            py: 1.5,
            fontSize: 16,
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
          }}
        >
          Load / Validate Configuration
        </Button>
        {ValidationDialog}
      </>
    );
  }

  // ── Button (full mode) ────────────────────────────────────────────────────
  return (
    <>
      <input
        type="file"
        accept=".cfg"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileSelect}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            fontWeight: 'bold',
            borderRadius: 8,
            px: 4,
            py: 1.5,
            fontSize: 16,
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
          }}
        >
          Load / Validate Configuration
        </Button>
      </Box>
      {ValidationDialog}
    </>
  );
};

export const LoadConfigButton: React.FC<{ onConfigLoaded?: (data: any) => void; sx?: any }> = ({ sx }) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        type="file"
        accept=".cfg"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={() => {}}
      />
      <Button
        variant="contained"
        color="primary"
        startIcon={<UploadFileIcon />}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          fontWeight: 'bold',
          borderRadius: 8,
          px: 4,
          py: 1.5,
          fontSize: 16,
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
          ...sx,
        }}
      >
        Load / Validate Configuration
      </Button>
    </>
  );
};

export default LoadConfigFile;
